import type { FC, ReactNode } from 'react'
import type { Me, MeResponse } from '@/types'

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/client'
import { setSoundsEnabled } from '@/lib/sounds'

export type WalletTab = 'deposit' | 'withdraw' | 'promo' | 'history'

export type ModalState =
    | { name: 'auth', mode?: 'signin' | 'signup' }
    | { name: 'profile' }
    | { name: 'wallet', tab?: WalletTab }
    | { name: 'settings' }
    | { name: 'stats' }
    | { name: 'chat' }

type SessionValue = {
    user: Me | null
    loading: boolean
    maintenance: boolean
    modal: ModalState | null
    openModal: (modal: ModalState) => void
    closeModal: () => void
    refresh: () => Promise<Me | null>
    setUser: (user: Me) => void
    setBalance: (balance: number) => void
    ensurePlayer: () => boolean
    signOut: () => Promise<void>
}

interface SessionProviderProps {
    children: ReactNode
}

const SessionContext = createContext<SessionValue | null>(null)

// Holds the signed-in player, their balance and which popup is open. Starts empty on the server and
// loads the session in the browser after the first render, so server and client markup always match.
export const SessionProvider: FC<SessionProviderProps> = ({ children }): ReactNode => {
    const [user, setUserState] = useState<Me | null>(null)
    const [loading, setLoading] = useState(true)
    const [maintenance, setMaintenance] = useState(false)
    const [modal, setModal] = useState<ModalState | null>(null)

    const refresh = useCallback(async (): Promise<Me | null> => {
        try {
            const data = await api<MeResponse>('/api/me')

            setUserState(data.user)
            setMaintenance(data.maintenance)

            return data.user
        }
        catch {
            return null
        }
        finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void refresh()
    }, [refresh])

    const signedIn = Boolean(user)

    // Balances change when staff approve deposits, so keep them fresh while the tab is open.
    useEffect(() => {
        if (!signedIn) return

        const timer = window.setInterval(() => {
            if (document.visibilityState === 'visible') void refresh()
        }, 20_000)

        const onFocus = () => void refresh()

        window.addEventListener('focus', onFocus)

        return () => {
            window.clearInterval(timer)
            window.removeEventListener('focus', onFocus)
        }
    }, [signedIn, refresh])

    const needsProfile = Boolean(user && !user.username)

    useEffect(() => {
        if (needsProfile) setModal({ name: 'profile' })
    }, [needsProfile])

    useEffect(() => {
        setSoundsEnabled(user?.soundsEnabled ?? true)
    }, [user?.soundsEnabled])

    const openModal = useCallback((next: ModalState) => setModal(next), [])
    const closeModal = useCallback(() => setModal(null), [])
    const setUser = useCallback((next: Me) => setUserState(next), [])
    const setBalance = useCallback((balance: number) => setUserState((current) => (current ? { ...current, balance } : current)), [])

    const ensurePlayer = useCallback((): boolean => {
        if (!user) {
            setModal({ name: 'auth', mode: 'signin' })

            return false
        }

        if (!user.username) {
            setModal({ name: 'profile' })

            return false
        }

        return true
    }, [user])

    const signOut = useCallback(async () => {
        await api('/api/auth/logout', { method: 'POST' }).catch(() => null)
        setUserState(null)
        setModal(null)
    }, [])

    const value = useMemo<SessionValue>(
        () => ({ user, loading, maintenance, modal, openModal, closeModal, refresh, setUser, setBalance, ensurePlayer, signOut }),
        [user, loading, maintenance, modal, openModal, closeModal, refresh, setUser, setBalance, ensurePlayer, signOut]
    )

    return (
        <SessionContext.Provider value={value}>
            {children}
        </SessionContext.Provider>
    )
}

export function useSession(): SessionValue {
    const value = useContext(SessionContext)

    if (!value) throw new Error('useSession must be used inside SessionProvider')

    return value
}
